import React, {useState, useCallback, useEffect, useMemo, useRef} from 'react';
import {useForm, Controller} from 'react-hook-form';
import {Box, Typography} from '@mui/material';
import ArrCheckbox from '../../../../common/arr-checkbox/ArrCheckbox';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrGroupedSelectDropdown from '../../../../common/arr-select-dropdown/ArrGroupedSelectDropdown';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrRadio from '../../../../common/arr-radio/ArrRadio';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {ISelectItem} from '../../../../../shared/types';
import ArrDataGridPro from '../../../../common/arr-data-grid-pro/ArrDataGridPro'; //
import {GridRenderCellParams} from '@mui/x-data-grid';
import './DatComponent.scss';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import TagsTreePanel from '../../../create-new-production/common/tags-tree-panel/TagsTreePanel';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import ArrFilterChip from '../../../../common/arr-filter-chip/ArrFilterChip';
import {t} from 'i18next';
import {useGetDatComponentReproduction} from '../../../../../hooks/productions/useReProductions';
import {GetDatComponentResponse} from '../../../../../api/types/production.type';
import {ModuleType, FormMode} from '../../ReProductionUtils';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import MenuIcon from '@mui/icons-material/Menu';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';

const RowReorderIcon: React.FC<any> = (props) => (
  <MenuIcon {...props} fontSize="small" />
);

interface DATField {
  id: number;
  category: string;
  sourceField: string;
  datFieldHeaderName: string;
  scrubRedactions: boolean;
  scrubPriv: boolean;
  selectedTags?: {id: string; label: string}[];
}

interface DatComponentProps {
  productionId: number;
  isProductionSet: boolean;
  exportProductionId?: number;
  tagTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  onFieldsChange?: (fields: DATField[]) => void;
  sharedHydrationRef?: React.MutableRefObject<boolean>;
  onHydrationComplete?: () => void;
  mode?: FormMode;
  isTemplate?: boolean;
  templateId?: number;
  scrubEditable?: boolean;
  isReProduction?: boolean; // ← ADD THIS
}

const DatComponent: React.FC<DatComponentProps> = ({
  productionId,
  //isProductionSet,
  exportProductionId = 0,
  tagTreeData = [],
  onFieldsChange,
  sharedHydrationRef,
  onHydrationComplete,
  mode = FormMode.CREATE,
  isTemplate = false,
  //templateId,
  scrubEditable = false,
  isReProduction = false, // ← ADD THIS
}) => {
  const isViewMode = mode === FormMode.VIEW;

  const isReProductionViewMode = isViewMode && isReProduction;
  //AVR-25006
  const isScrubLocked = isReProductionViewMode && !scrubEditable;

  // Function to get current DAT configuration
  const getCurrentConfig = () => {
    return {
      ansiFormatType: encodingDropdownValue?.id,
      fieldSeparator: fieldSeparator?.id
        ? parseInt(fieldSeparator.id.toString())
        : undefined,
      textQualifier: textQualifier?.id
        ? parseInt(textQualifier.id.toString())
        : undefined,
      newLineSeparator: newLine?.id
        ? parseInt(newLine.id.toString())
        : undefined,
      multiValue: multiValue?.id
        ? parseInt(multiValue.id.toString())
        : undefined,
      dateFormatID: dateFormat?.id
        ? parseInt(dateFormat.id.toString())
        : undefined,
      dateFormat: dateFormat?.label,
      overwriteMasterDate: false, // TODO: Add state variable for this
      isAnsi: encoding === 'rdbANSI', // true if ANSI is selected
      eTextFormat: encoding === 'lstANSIType' ? 1 : 0, // 1 if Unicode, 0 if ANSI
      prependCarriageReturn: false, // TODO: Add state variable for this
    };
  };

  // Expose getCurrentConfig function globally for parent to access
  (window as any).getDatConfig = getCurrentConfig;
  // Get Redux state
  const selectedSecurityGroupId = useSelector(
    (state: RootState) => state.production.securityGroupId
  );
  const selectedProductionSetId = useSelector(
    (state: RootState) => state.production.productionSetId
  );

  // API call to get DAT component data
  const {
    data: datComponentDataFromProduction,
    isLoading: isDatComponentLoadingFromProduction,
    error: datComponentErrorFromProduction,
  } = useGetDatComponentReproduction(
    {
      SecurityGroupId: selectedSecurityGroupId,
      moduletype: ModuleType.REPRODUCTION,
      parentsetid: selectedProductionSetId,
      productionId: productionId,
      exportProductionId: exportProductionId || 0,
    },
    !!selectedSecurityGroupId &&
      !!selectedProductionSetId &&
      !isTemplate &&
      productionId > 0
  );

  const datComponentData: GetDatComponentResponse | undefined = useMemo(() => {
    return datComponentDataFromProduction;
  }, [datComponentDataFromProduction]);

  const isDatComponentLoading = isDatComponentLoadingFromProduction;
  const datComponentError = datComponentErrorFromProduction;

  // State for dropdown options (populated from API)
  const [ansiFormatOptions, setAnsiFormatOptions] = useState<ISelectItem[]>([]);
  const [dateFormatOptions, setDateFormatOptions] = useState<ISelectItem[]>([]);
  const [fieldSeparatorOptions, setFieldSeparatorOptions] = useState<
    ISelectItem[]
  >([]);
  const [multiValueOptions, setMultiValueOptions] = useState<ISelectItem[]>([]);
  const [textQualifierOptions, setTextQualifierOptions] = useState<
    ISelectItem[]
  >([]);
  const [newLineOptions, setNewLineOptions] = useState<ISelectItem[]>([]);
  // Static category options for table dropdown
  const categoryOptions: ISelectItem[] = [
    {id: 'Field', name: 'Field'},
    {id: 'Tags', name: 'Tags'},
  ];
  const [groupedFieldOptions, setGroupedFieldOptions] = useState<any[]>([]);
  const [, setComponentIsDirty] = useState<boolean>(false);
  const isHydratingFromApiRef = useRef(true);

  // Populate dropdown options from API response
  useEffect(() => {
    if (!datComponentData) return;
    isHydratingFromApiRef.current = true;
    if (sharedHydrationRef) sharedHydrationRef.current = true;
    // ANSI Format options
    const ansiOptions =
      datComponentData.ansiFormatList?.map((item) => ({
        id: item.ansiTypeID,
        name: item.ansiTypeLabel,
      })) || [];
    setAnsiFormatOptions(ansiOptions);

    // Set initial encoding (ANSI/Unicode) based on API response eTextFormat
    // eTextFormat: 1 = Unicode, 0 = ANSI
    if (datComponentData.modelData?.eTextFormat === 1) {
      setEncoding('lstANSIType');
    } else {
      setEncoding('rdbANSI');
    }

    // Set default ANSI selection based on datSelectedANSI
    if (datComponentData.datSelectedANSI && ansiOptions.length > 0) {
      const defaultAnsi = ansiOptions.find(
        (option) => option.id === datComponentData.datSelectedANSI
      );
      if (defaultAnsi) {
        setEncodingDropdownValue(defaultAnsi);
      } else if (ansiOptions.length > 0) {
        // Fallback to first option if datSelectedANSI not found
        setEncodingDropdownValue(ansiOptions[0]);
      }
    } else if (ansiOptions.length > 0) {
      // Fallback to first option if no datSelectedANSI
      setEncodingDropdownValue(ansiOptions[0]);
    }

    // Date Format options
    const dateOptions =
      datComponentData.dateFormats?.map((item) => ({
        id: item.value.toString(),
        name: item.text,
      })) || [];
    setDateFormatOptions(dateOptions);

    // Set initial date format selection based on modelData.dateFormatID
    if (datComponentData.modelData?.dateFormatID && dateOptions.length > 0) {
      const defaultDateFormat = dateOptions.find(
        (option) =>
          option.id === datComponentData.modelData.dateFormatID.toString()
      );
      if (defaultDateFormat) {
        setDateFormat(defaultDateFormat);
      } else if (dateOptions.length > 0) {
        // Fallback to first option if dateFormatID not found
        setDateFormat(dateOptions[0]);
      }
    } else if (dateOptions.length > 0) {
      // Fallback to first option if no dateFormatID
      setDateFormat(dateOptions[0]);
    }

    // Field Separator options
    const fieldSeparatorOptions =
      datComponentData.columnDelimiterList?.map((item) => ({
        id: item.value,
        name: item.text,
      })) || [];
    setFieldSeparatorOptions(fieldSeparatorOptions);

    // Multi Value options
    const multiValueOptions =
      datComponentData.dataDelimiterList?.map((item) => ({
        id: item.value,
        name: item.text,
      })) || [];
    setMultiValueOptions(multiValueOptions);

    // Text Qualifier options
    const textQualifierOptions =
      datComponentData.textQualifierList?.map((item) => ({
        id: item.value,
        name: item.text,
      })) || [];
    setTextQualifierOptions(textQualifierOptions);

    // New Line options
    const newLineOptions =
      datComponentData.multiline?.map((item) => ({
        id: item.value,
        name: item.text,
      })) || [];
    setNewLineOptions(newLineOptions);

    // Set initial field separator selection based on modelData.fieldSeparator
    if (
      datComponentData.modelData?.fieldSeparator !== undefined &&
      fieldSeparatorOptions.length > 0
    ) {
      const defaultFieldSeparator = fieldSeparatorOptions.find(
        (option) =>
          option.id === datComponentData.modelData.fieldSeparator.toString()
      );
      if (defaultFieldSeparator) {
        setFieldSeparator(defaultFieldSeparator);
      } else if (fieldSeparatorOptions.length > 0) {
        setFieldSeparator(fieldSeparatorOptions[0]);
      }
    } else if (fieldSeparatorOptions.length > 0) {
      setFieldSeparator(fieldSeparatorOptions[0]);
    }

    // Set initial multi value selection based on modelData.multiValue
    if (
      datComponentData.modelData?.multiValue !== undefined &&
      multiValueOptions.length > 0
    ) {
      const defaultMultiValue = multiValueOptions.find(
        (option) =>
          option.id === datComponentData.modelData.multiValue.toString()
      );
      if (defaultMultiValue) {
        setMultiValue(defaultMultiValue);
      } else if (multiValueOptions.length > 0) {
        setMultiValue(multiValueOptions[0]);
      }
    } else if (multiValueOptions.length > 0) {
      setMultiValue(multiValueOptions[0]);
    }

    // Set initial text qualifier selection based on modelData.textQualifier
    if (
      datComponentData.modelData?.textQualifier !== undefined &&
      textQualifierOptions.length > 0
    ) {
      const defaultTextQualifier = textQualifierOptions.find(
        (option) =>
          option.id === datComponentData.modelData.textQualifier.toString()
      );
      if (defaultTextQualifier) {
        setTextQualifier(defaultTextQualifier);
      } else if (textQualifierOptions.length > 0) {
        setTextQualifier(textQualifierOptions[0]);
      }
    } else if (textQualifierOptions.length > 0) {
      setTextQualifier(textQualifierOptions[0]);
    }

    // Set initial new line selection based on modelData.newLineSeparator
    if (
      datComponentData.modelData?.newLineSeparator !== undefined &&
      newLineOptions.length > 0
    ) {
      const defaultNewLine = newLineOptions.find(
        (option) =>
          option.id === datComponentData.modelData.newLineSeparator.toString()
      );
      if (defaultNewLine) {
        setNewLine(defaultNewLine);
      } else if (newLineOptions.length > 0) {
        setNewLine(newLineOptions[0]);
      }
    } else if (newLineOptions.length > 0) {
      setNewLine(newLineOptions[0]);
    }

    // Category options are static - no need to set from API

    // Populate fields from datFieldMappingDataList
    if (
      datComponentData.modelData?.datFieldMappingDataList &&
      datComponentData.modelData.datFieldMappingDataList.length > 0
    ) {
      const mappedFields: DATField[] =
        datComponentData.modelData.datFieldMappingDataList.map(
          (item, index) => {
            const isTagsCategory =
              item.selectedTypeId?.toLowerCase() === 'tags';
            let selectedTags: {id: string; label: string}[] = [];

            // If it's a tags category and selectedSourceId contains comma-separated tag IDs
            if (isTagsCategory && item.selectedSourceId) {
              const tagIds = item.selectedSourceId
                .split(',')
                .map((id) => id.trim());

              // Only process tags if we have tag tree data available
              if (tagTreeData && tagTreeData.length > 0) {
                // Flatten the tag tree to find matching tags
                const flattenTree = (
                  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
                ): ExtendedTreeItemProps[] => {
                  return nodes.reduce<ExtendedTreeItemProps[]>((acc, node) => {
                    acc.push(node);
                    if (node.children) acc.push(...flattenTree(node.children));
                    return acc;
                  }, []);
                };

                const flatTree = flattenTree(tagTreeData);

                selectedTags = flatTree
                  .filter((node) => tagIds.includes(node.id))
                  .map((node) => ({id: node.id, label: node.label}));
              } else {
                selectedTags = [];
              }
            }

            return {
              id: index + 1,
              category: isTagsCategory ? 'Tags' : 'Field',
              sourceField: item.selectedSourceId,
              datFieldHeaderName: item.datField,
              scrubRedactions: item.isRedacted,
              scrubPriv: item.isPrivileged,
              selectedTags: selectedTags,
            };
          }
        );
      setFields(mappedFields);
    } else {
      setFields([
        {
          id: 1,
          category: 'Field',
          sourceField: '-146',
          datFieldHeaderName: 'ProdBeginningBates',
          scrubRedactions: false,
          scrubPriv: false,
          selectedTags: [] as {id: string; label: string}[],
        },
      ]);
    }

    // Grouped field options from datTypeList (filter out TAGS typeId)
    const groupedOptions =
      datComponentData.modelData?.datTypeList
        ?.filter((type) => type.typeId !== 'TAGS')
        ?.map((type) => ({
          groupName: type.typeLabel,
          items:
            type.datSourceTypeList?.map((source) => ({
              id: source.value,
              label: source.text,
            })) || [],
        })) || [];
    setGroupedFieldOptions(groupedOptions);

    const timeoutId = window.setTimeout(() => {
      isHydratingFromApiRef.current = false;
      setComponentIsDirty(false);
      onHydrationComplete?.();
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [datComponentData, sharedHydrationRef, onHydrationComplete]);

  // Re-process fields when tagTreeData becomes available
  useEffect(() => {
    if (
      datComponentData?.modelData?.datFieldMappingDataList &&
      tagTreeData &&
      tagTreeData.length > 0
    ) {
      isHydratingFromApiRef.current = true;
      if (sharedHydrationRef) sharedHydrationRef.current = true;

      setFields((currentFields) => {
        const updatedFields = currentFields.map((field) => {
          // Only process tags category fields that don't have selectedTags yet
          if (
            field.category === 'Tags' &&
            field.sourceField &&
            (!field.selectedTags || field.selectedTags.length === 0)
          ) {
            const tagIds = field.sourceField.split(',').map((id) => id.trim());

            // Flatten the tag tree to find matching tags
            const flattenTree = (
              nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
            ): ExtendedTreeItemProps[] => {
              return nodes.reduce<ExtendedTreeItemProps[]>((acc, node) => {
                acc.push(node);
                if (node.children) acc.push(...flattenTree(node.children));
                return acc;
              }, []);
            };

            const flatTree = flattenTree(tagTreeData);
            const selectedTags = flatTree
              .filter((node) => tagIds.includes(node.id))
              .map((node) => ({id: node.id, label: node.label}));

            return {
              ...field,
              selectedTags: selectedTags,
            };
          }
          return field;
        });

        // Only update if there are changes
        const hasChanges = updatedFields.some((field) => {
          const original = currentFields.find((f) => f.id === field.id);
          return field.selectedTags?.length !== original?.selectedTags?.length;
        });

        return hasChanges ? updatedFields : currentFields;
      });

      const timeoutId = window.setTimeout(() => {
        isHydratingFromApiRef.current = false;
        setComponentIsDirty(false);
        onHydrationComplete?.();
      }, 150);

      return () => window.clearTimeout(timeoutId);
    }
  }, [tagTreeData, datComponentData, sharedHydrationRef, onHydrationComplete]);

  const [encoding, setEncoding] = useState('rdbANSI');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<number | null>(null);

  const [encodingDropdownValue, setEncodingDropdownValue] =
    useState<ISelectItem | null>(null);
  const [dateFormat, setDateFormat] = useState<ISelectItem | null>(null);
  const [fieldSeparator, setFieldSeparator] = useState<ISelectItem | null>(
    null
  );
  const [multiValue, setMultiValue] = useState<ISelectItem | null>(null);
  const [textQualifier, setTextQualifier] = useState<ISelectItem | null>(null);
  const [newLine, setNewLine] = useState<ISelectItem | null>(null);

  const {control} = useForm({
    defaultValues: {
      encoding,
      encodingDropdownValue,
      dateFormat,
      fieldSeparator,
      multiValue,
      textQualifier,
      newLine,
      formDirty: false,
    },
  });
  // Determine default field based on moduleType
  const getDefaultField = () => {
    // For REPRODUCTION, use ProdBeginningBates as default
    return {
      id: Date.now(),
      category: 'Field',
      sourceField: '-146',
      datFieldHeaderName: 'ProdBeginningBates',
      scrubRedactions: false,
      scrubPriv: false,
      selectedTags: [] as {id: string; label: string}[],
    };
    //}
  };

  const [fields, setFields] = useState<DATField[]>([getDefaultField()]);

  // Notify parent component when fields change
  useEffect(() => {
    if (!onFieldsChange) return;

    const timeoutId = window.setTimeout(() => {
      onFieldsChange(fields);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [fields, onFieldsChange]);

  // State for tag panel
  const [isTagPanelOpen, setIsTagPanelOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const [currentFieldId, setCurrentFieldId] = useState<number | null>(null);

  // Handle opening tag panel for a specific field
  const handleOpenTagPanel = (fieldId: number) => {
    setCurrentFieldId(fieldId);
    setComponentIsDirty(true);
    const field = fields.find((f) => f.id === fieldId);
    if (field) {
      // Find matching tags in the tree data to preserve full structure
      const preSelectedTags: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

      if (field.selectedTags && field.selectedTags.length > 0) {
        // Flatten the tree to find matching tags
        const flattenTree = (
          nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
        ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
          return nodes.reduce<TreeViewBaseItem<ExtendedTreeItemProps>[]>(
            (acc, node) => {
              acc.push(node);
              if (node.children) acc.push(...flattenTree(node.children));
              return acc;
            },
            []
          );
        };

        const flatTree = flattenTree(tagTreeData);

        // Find matching tags in the tree and add them to preSelectedTags
        field.selectedTags.forEach((selectedTag) => {
          const matchingTreeTag = flatTree.find(
            (treeTag) => treeTag.id === selectedTag.id
          );
          if (matchingTreeTag) {
            preSelectedTags.push(matchingTreeTag);
          }
        });
      }

      setSelectedTags(preSelectedTags);
      setIsTagPanelOpen(true);
    } else {
      setSelectedTags([]);
      setIsTagPanelOpen(true);
    }
  };

  // Handle tag selection from panel
  const handleTagSelection = (
    tags: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ) => {
    if (currentFieldId !== null) {
      // Filter out parent tags and only keep child tags (tags without children)
      const childTags = tags.filter(
        (tag) => !tag.children || tag.children.length === 0
      );

      // Convert tree format back to simple format
      const simpleTags = childTags.map((tag) => ({
        id: tag.id,
        label: tag.label,
      }));

      // Update the specific field with selected tags and set header name to "Tags"
      setFields((prev) =>
        prev.map((field) =>
          field.id === currentFieldId
            ? {
                ...field,
                selectedTags: simpleTags,
                datFieldHeaderName:
                  simpleTags.length > 0 ? 'Tags' : field.datFieldHeaderName,
              }
            : field
        )
      );
    }
    setIsTagPanelOpen(false);
    setCurrentFieldId(null);
  };

  const addField = useCallback(() => {
    setFields((prev) => {
      const isFirstRow = prev.length === 0;
      setComponentIsDirty(true);

      // When the grid is empty and a new row is added, default to
      // ProdBeginningBates for PRODUCTION and DocID for EXPORT.
      if (isFirstRow) {
        // Try to resolve DocID from grouped options for EXPORT
        let defaultSourceField = '';
        let defaultHeaderName = '';

        defaultSourceField = '-146';
        defaultHeaderName = 'ProdBeginningBates';
        return [
          {
            id: 1,
            category: 'Field',
            sourceField: defaultSourceField,
            datFieldHeaderName: defaultHeaderName,
            scrubRedactions: false,
            scrubPriv: false,
            selectedTags: [] as {id: string; label: string}[],
          },
        ];
      }

      // For additional rows, keep them empty by default
      const nextId = prev.length ? prev[prev.length - 1].id + 1 : 1;
      return [
        ...prev,
        {
          id: nextId,
          category: 'Field',
          sourceField: '',
          datFieldHeaderName: '',
          scrubRedactions: false,
          scrubPriv: false,
        },
      ];
    });
  }, [groupedFieldOptions]);

  const updateField = useCallback(
    (id: number, key: keyof DATField, value: any) => {
      setFields((prev) =>
        prev.map((f) => (f.id === id ? {...f, [key]: value} : f))
      );
    },
    []
  );

  const removeField = (id: number) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
  };

  // Show loading state
  if (isDatComponentLoading || datComponentError || !datComponentData) {
    return <ArrLoadingSpinner />;
  }

  return (
    <>
      <Box
        className={[
          'dat-component',
          isReProductionViewMode ? 'dat-component--view-mode' : '',
          isScrubLocked ? 'dat-component--scrub-locked' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/* Grid layout */}
        <Box className="dat-grid">
          {/* Encoding Section */}
          <Box className="dat-grid-encoding">
            <Box className="dat-grid-encoding-header">
              <Typography className="typo-text label2_size">
                {t(
                  'productionsAndExports.createNewProduction.datComponent.encoding'
                )}
                <ArrTooltipWrapper
                  trigger={<InfoOutlinedIcon className="dat-info-icon" />}
                >
                  {t(
                    'productionsAndExports.createNewProduction.datComponent.encodingTooltip'
                  )}
                </ArrTooltipWrapper>
              </Typography>
            </Box>

            <Box className="dat-radio-group">
              <Controller
                control={control}
                name="encoding"
                render={({field}) => (
                  <ArrRadio
                    dataTestId="encoding-field-1"
                    name="encoding"
                    value={encoding === 'rdbANSI' ? 'rdbANSI' : null}
                    onChange={(val: any) => {
                      field.onChange(val);
                      setEncoding(val);
                      setComponentIsDirty(true);
                    }}
                    data={[
                      {
                        label: t(
                          'productionsAndExports.createNewProduction.datComponent.ansiLabel'
                        ),
                        value: 'rdbANSI',
                      },
                    ]}
                    size="small"
                    disabled={isViewMode}
                  />
                )}
              />

              <Controller
                control={control}
                name="encodingDropdownValue"
                render={({field}) => (
                  <ArrSingleSelectDropdown
                    dropdownLabel={t(
                      'productionsAndExports.createNewProduction.datComponent.selectAnsi'
                    )}
                    dropdownItems={ansiFormatOptions}
                    selectedValues={field.value || encodingDropdownValue}
                    onSelectionChange={(item) => {
                      if (isViewMode) return;
                      setComponentIsDirty(true);
                      item &&
                        (field.onChange(item), setEncodingDropdownValue(item));
                    }}
                    sizeVariant="medium"
                    disabled={encoding === 'lstANSIType' || isViewMode}
                    dataTestId="asd-elstANSIType"
                    searchable
                    showNoResultsText
                  />
                )}
              />

              <Controller
                control={control}
                name="encoding"
                render={({field}) => (
                  <ArrRadio
                    dataTestId="encoding-field-2"
                    name="encoding"
                    value={encoding}
                    onChange={(val: any) => {
                      setEncoding(val);
                      field.onChange(val);
                      setComponentIsDirty(true);
                    }}
                    data={[
                      {
                        label: t(
                          'productionsAndExports.createNewProduction.datComponent.unicodeLabel'
                        ),
                        value: 'lstANSIType',
                      },
                    ]}
                    size="small"
                    disabled={isViewMode}
                  />
                )}
              />
            </Box>
          </Box>

          {/* Date Format Section */}
          <Box className="dat-grid-dateformat">
            <Box className="dat-grid-date-header">
              <Typography className="typo-text label2_size">
                {t(
                  'productionsAndExports.createNewProduction.datComponent.dateFormat'
                )}
                <ArrTooltipWrapper
                  trigger={<InfoOutlinedIcon className="dat-info-icon" />}
                >
                  {t(
                    'productionsAndExports.createNewProduction.datComponent.dateFormatTooltip'
                  )}
                </ArrTooltipWrapper>
              </Typography>
            </Box>
            <Box className="dat-date-format">
              <Controller
                control={control}
                name="dateFormat"
                render={({field}) => (
                  <ArrSingleSelectDropdown
                    dropdownLabel="select format"
                    dropdownItems={dateFormatOptions}
                    selectedValues={field.value || dateFormat || null}
                    onSelectionChange={(item) => {
                      if (isViewMode) return;
                      item && (field.onChange(item), setDateFormat(item));
                      setComponentIsDirty(true);
                    }}
                    sizeVariant="medium"
                    dataTestId="asd-lstDateFormat"
                    disabled={isViewMode}
                    searchable
                    showNoResultsText
                  />
                )}
              />
            </Box>
          </Box>

          {/* Field Delimiters */}
          <Box className="dat-grid-field-delimiters-header">
            <Typography className="typo-text label2_size">
              {t(
                'productionsAndExports.createNewProduction.datComponent.fieldDelimiters'
              )}
              <ArrTooltipWrapper
                trigger={<InfoOutlinedIcon className="dat-info-icon" />}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'productionsAndExports.createNewProduction.datComponent.fieldDelimitersTooltip'
                    ),
                  }}
                />
              </ArrTooltipWrapper>
            </Typography>
          </Box>

          <Box className="dat-grid-field-delimiters">
            <Box className="dat-delimiter-inline">
              <span className="typo-text label2_size">
                {t(
                  'productionsAndExports.createNewProduction.datComponent.fieldSeparator'
                )}
              </span>
              <Controller
                control={control}
                name="fieldSeparator"
                render={({field}) => (
                  <ArrSingleSelectDropdown
                    dropdownLabel=""
                    dropdownItems={fieldSeparatorOptions}
                    selectedValues={field.value || fieldSeparator || null}
                    onSelectionChange={(item) => {
                      if (isViewMode) return;
                      item && (field.onChange(item), setFieldSeparator(item));
                    }}
                    sizeVariant="medium"
                    dataTestId="asd-lstFieldSeparator"
                    disabled={isViewMode}
                    searchable
                    showNoResultsText
                  />
                )}
              />
            </Box>

            <Box className="dat-delimiter-inline">
              <span className="typo-text label2_size">
                {t(
                  'productionsAndExports.createNewProduction.datComponent.multiValue'
                )}
              </span>
              <Controller
                control={control}
                name="multiValue"
                render={({field}) => (
                  <ArrSingleSelectDropdown
                    dropdownLabel=""
                    dropdownItems={multiValueOptions}
                    selectedValues={field.value || multiValue || null}
                    onSelectionChange={(item) => {
                      if (isViewMode) return;
                      item && (field.onChange(item), setMultiValue(item));
                    }}
                    sizeVariant="medium"
                    dataTestId="asd-lstMultiValue"
                    disabled={isViewMode}
                    searchable
                    showNoResultsText
                  />
                )}
              />
            </Box>

            <Box className="dat-delimiter-inline">
              <span className="typo-text label2_size">
                {t(
                  'productionsAndExports.createNewProduction.datComponent.textQualifier'
                )}
              </span>
              <Controller
                control={control}
                name="textQualifier"
                render={({field}) => (
                  <ArrSingleSelectDropdown
                    dropdownLabel=""
                    dropdownItems={textQualifierOptions}
                    selectedValues={field.value || textQualifier || null}
                    onSelectionChange={(item) => {
                      if (isViewMode) return;
                      item && (field.onChange(item), setTextQualifier(item));
                    }}
                    sizeVariant="medium"
                    dataTestId="asd-lstTextQualifier"
                    disabled={isViewMode}
                    searchable
                    showNoResultsText
                  />
                )}
              />
            </Box>

            <Box className="dat-delimiter-inline">
              <span className="typo-text label2_size">
                {t(
                  'productionsAndExports.createNewProduction.datComponent.newLine'
                )}
              </span>
              <Controller
                control={control}
                name="newLine"
                render={({field}) => (
                  <ArrSingleSelectDropdown
                    dropdownLabel=""
                    dropdownItems={newLineOptions}
                    selectedValues={field.value || newLine || null}
                    onSelectionChange={(item) => {
                      if (isViewMode) return;
                      item && (field.onChange(item), setNewLine(item));
                    }}
                    sizeVariant="medium"
                    dataTestId="asd-lstNewLineSeparator"
                    disabled={isViewMode}
                    searchable
                    showNoResultsText
                  />
                )}
              />
            </Box>
          </Box>
        </Box>

        {/* DAT Field Mapping Grid */}
        <Box className="dat-field-mapping">
          <Typography className="typo-text subhead2_size">
            {t(
              'productionsAndExports.createNewProduction.datComponent.datFieldMapping'
            )}
            <ArrTooltipWrapper
              trigger={<InfoOutlinedIcon className="dat-info-icon" />}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    'productionsAndExports.createNewProduction.datComponent.datFieldMappingTooltip'
                  ),
                }}
              />
            </ArrTooltipWrapper>
          </Typography>

          <Box className="dat-grid-table">
            <ArrDataGridPro
              rows={fields}
              rowReordering={!isViewMode}
              rowSelection={false}
              getRowHeight={() => 'auto'}
              autoHeight={true}
              getRowClassName={(params: any) =>
                params?.row?.category === 'Tags' &&
                Array.isArray(params?.row?.selectedTags) &&
                params.row.selectedTags.length > 0
                  ? 'dat-tags-row'
                  : ''
              }
              currentPage={1}
              showColumnVerticalBorder={false}
              disableColumnSorting={true}
              disableColumnFilter={true}
              slots={{rowReorderIcon: RowReorderIcon as any}}
              onSelectionStateChange={() => {}}
              disableVirtualization
              disableColumnReorder={true}
              columns={[
                {
                  field: '__reorder__',
                  headerName: '',
                  width: 40,
                  sortable: false,
                  filterable: false,
                  renderCell: () => <RowReorderIcon />,
                },
                {
                  field: 'category',
                  headerName: t(
                    'productionsAndExports.createNewProduction.datComponent.columns.category'
                  ),
                  flex: 0.7,
                  renderCell: (params) => (
                    <ArrSingleSelectDropdown
                      dropdownLabel="select"
                      dropdownItems={categoryOptions}
                      selectedValues={
                        categoryOptions.find(
                          (opt) => opt.id === params.row.category
                        ) || null
                      }
                      onSelectionChange={(opt) => {
                        if (!opt) return;
                        setComponentIsDirty(true);
                        updateField(params.row.id, 'category', opt.id);
                        if (opt.id === 'Tags') {
                          updateField(
                            params.row.id,
                            'datFieldHeaderName',
                            'Tags'
                          );
                        } else if (opt.id === 'Field') {
                          updateField(params.row.id, 'datFieldHeaderName', '');
                          updateField(params.row.id, 'sourceField', '');
                        }
                      }}
                      sizeVariant="small"
                      dataTestId={`asd-hdnSequenceNumber_${params.row.id}`}
                      disabled={isViewMode}
                    />
                  ),
                },
                {
                  field: 'sourceField',
                  headerName: t(
                    'productionsAndExports.createNewProduction.datComponent.columns.sourceField'
                  ),
                  flex: 0.9,
                  renderCell: (params: GridRenderCellParams) => {
                    const isTagsCategory = params.row.category === 'Tags';
                    return isTagsCategory ? (
                      <Box className="dat-grid-sourceField">
                        {!isViewMode && (
                          <ArrButton
                            variant="secondary"
                            size="small"
                            onClick={() => {
                              setComponentIsDirty(true);
                              handleOpenTagPanel(params.row.id);
                            }}
                            dataTestId={`SF_${params.row.id}`}
                          >
                            {t(
                              'productionsAndExports.createNewProduction.datComponent.selectTag'
                            )}
                          </ArrButton>
                        )}

                        {/* Display selected tags using ArrFilterChip styles */}
                        <Box className="arr-filter-chip-v1-selection-block">
                          {params.row.selectedTags?.map(
                            (tag: {id: string; label: string}) => (
                              <ArrFilterChip
                                key={tag.id}
                                filterName=""
                                selections={[{id: tag.id, value: tag.label}]}
                                variant="v1"
                                operator=""
                                isResponsive={false}
                                disabled={isViewMode}
                                onRemove={() => {
                                  setComponentIsDirty(true);
                                  updateField(
                                    params.row.id,
                                    'selectedTags',
                                    params.row.selectedTags?.filter(
                                      (t: {id: string; label: string}) =>
                                        t.id !== tag.id
                                    )
                                  );
                                }}
                                dataTestId={'ArrFilterChip_' + tag.id}
                              />
                            )
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <ArrGroupedSelectDropdown
                        dropdownLabel="Select"
                        groupedItems={groupedFieldOptions}
                        selectedValue={params.row.sourceField}
                        onSelectionChange={(value) => {
                          setComponentIsDirty(true);
                          updateField(params.row.id, 'sourceField', value);
                          // Auto-populate DAT Field Header Name with the display name of the selected field
                          const selectedField = groupedFieldOptions
                            .flatMap((group) => group.items)
                            .find((item) => item.id === value);
                          const displayName = selectedField
                            ? selectedField.label
                            : value;
                          updateField(
                            params.row.id,
                            'datFieldHeaderName',
                            displayName
                          );
                        }}
                        searchable={true}
                        sizeVariant="small"
                        data-testid={`select2-SF_${params.row.id}-container`}
                        disabled={isViewMode}
                        dataTestId={`select2-SF_${params.row.id}-container`}
                      />
                    );
                  },
                },
                {
                  field: 'datFieldHeaderName',
                  headerName: t(
                    'productionsAndExports.createNewProduction.datComponent.columns.datFieldHeaderName'
                  ),
                  flex: 0.9,
                  renderCell: (params) => (
                    <ArrTextField
                      value={params.row.datFieldHeaderName}
                      onChange={(e) => {
                        setComponentIsDirty(true);
                        updateField(
                          params.row.id,
                          'datFieldHeaderName',
                          e.target.value
                        );
                      }}
                      sizeVariant="small"
                      dataTestId={`DATFL_${params.row.id}`}
                      state={isViewMode ? 'inactive' : 'enabled'}
                    />
                  ),
                },
                {
                  field: 'scrubRedactions',
                  headerName: t(
                    'productionsAndExports.createNewProduction.datComponent.columns.scrubRedactions'
                  ),
                  renderHeader: () => (
                    <span className="scrubHeader">
                      <span>
                        {t(
                          'productionsAndExports.createNewProduction.datComponent.columns.scrubRedactions'
                        )}
                      </span>
                      <span className="scrubTag">
                        {t(
                          'productionsAndExports.createNewProduction.datComponent.columns.scrubRedactionsSub'
                        )}
                      </span>
                    </span>
                  ),
                  flex: 0.6,
                  sortable: false,
                  renderCell: (params: GridRenderCellParams) => (
                    <ArrCheckbox
                      checked={params.row.scrubRedactions || false}
                      onChange={(checked: boolean) => {
                        setComponentIsDirty(true);
                        updateField(params.row.id, 'scrubRedactions', checked);
                      }}
                      labelText=""
                      size="small"
                      id={`ChkRedacted_${params.row.id}`}
                      disabled={isViewMode && !scrubEditable}
                      aria-label={t(
                        'productionsAndExports.createNewProduction.datComponent.columns.scrubRedactionsSub'
                      )}
                    />
                  ),
                },
                {
                  field: 'scrubPriv',
                  headerName: t(
                    'productionsAndExports.createNewProduction.datComponent.columns.scrubRedactions'
                  ),
                  renderHeader: () => (
                    <span className="scrubHeader">
                      <span>
                        {t(
                          'productionsAndExports.createNewProduction.datComponent.columns.scrubRedactions'
                        )}
                      </span>
                      <span className="scrubTag">
                        {t(
                          'productionsAndExports.createNewProduction.datComponent.columns.scrubPrevSub'
                        )}
                      </span>
                    </span>
                  ),
                  flex: 0.6,
                  sortable: false,
                  renderCell: (params: GridRenderCellParams) => (
                    <ArrCheckbox
                      checked={params.row.scrubPriv || false}
                      onChange={(checked: boolean) => {
                        setComponentIsDirty(true);
                        updateField(params.row.id, 'scrubPriv', checked);
                      }}
                      labelText=""
                      size="small"
                      id={`ChkPrev_${params.row.id}`}
                      disabled={isViewMode && !scrubEditable}
                      aria-label={t(
                        'productionsAndExports.createNewProduction.datComponent.columns.scrubPrevSub'
                      )}
                    />
                  ),
                },
                ...(!isViewMode
                  ? [
                      {
                        field: 'actions',
                        headerName: '',
                        flex: 0.1,
                        sortable: false,
                        filterable: false,
                        renderCell: (params: GridRenderCellParams) => (
                          <ArrButton
                            variant="text"
                            size="small"
                            onClick={() => {
                              setComponentIsDirty(true);
                              setRowToDelete(params.row.id);
                              setShowDeleteModal(true);
                            }}
                            startIcon={<DeleteOutlineOutlinedIcon />}
                            className="dat-delete-btn"
                            dataTestId={`dat-delete-btn-${params.id}`}
                          />
                        ),
                      },
                    ]
                  : []),
              ]}
            />

            {!isViewMode && (
              <Box className="dat-add-btn-container">
                <ArrButton
                  variant="primary"
                  size="medium"
                  onClick={addField}
                  className="dat-add-btn"
                  dataTestId="datAddFieldBtn"
                >
                  {t(
                    'productionsAndExports.createNewProduction.datComponent.addField'
                  )}
                </ArrButton>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {showDeleteModal && (
        <ArrModal
          showModal={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          headerTitle="Delete"
          className="dat-delete-modal"
          title={t(
            'productionsAndExports.createNewProduction.datComponent.deleteMsg'
          )}
          dataTestId="dat-delete-modal"
          buttons={[
            {
              id: '2',
              label: t(
                'productionsAndExports.createNewProduction.datComponent.cancel'
              ),
              onClick: () => setShowDeleteModal(false),
              variant: 'secondary',
              dataTestId: 'bot2-Msg1',
            },
            {
              id: '1',
              label: t(
                'productionsAndExports.createNewProduction.datComponent.delete'
              ),
              onClick: () => {
                if (rowToDelete !== null) removeField(rowToDelete);
                setShowDeleteModal(false);
                setRowToDelete(null);
              },
              variant: 'danger',

              dataTestId: 'bot1-Msg1',
            },
          ]}
        />
      )}

      {/* Tags Tree Panel */}
      <TagsTreePanel
        key={`tag-panel-${currentFieldId}-${selectedTags.length}`}
        tags={tagTreeData}
        type="flyout"
        isOpen={isTagPanelOpen}
        title={t(
          'productionsAndExports.createNewProduction.datComponent.selectTags'
        )}
        onClose={() => {
          setIsTagPanelOpen(false);
          setCurrentFieldId(null);
        }}
        selectedTags={selectedTags}
        setSelectedTags={handleTagSelection}
      />
    </>
  );
};

export default DatComponent;
